/**
 * Guest staging for sales-point contacts (plan §3.4 / §6.9).
 * Key `customer:pendingSpContacts`, max 20, TTL 90d, dedupe by salesPointId else tenantCode+slug.
 * No kiosk-guest Customer on catalog open without token.
 */

import type { PendingSpContactRef } from './salesPointContactsTypes.js';

export const PENDING_SP_CONTACTS_STORAGE_KEY = 'customer:pendingSpContacts';
export const PENDING_SP_CONTACTS_MAX = 20;
export const PENDING_SP_CONTACTS_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface StoredPendingSpContact extends PendingSpContactRef {
  /** Epoch ms when staged — used for TTL. */
  storedAt: number;
}

function dedupeKey(contact: PendingSpContactRef): string {
  if (typeof contact.salesPointId === 'number' && contact.salesPointId > 0) {
    return `id:${contact.salesPointId}`;
  }
  const slug = (contact.salesPointSlug ?? '').trim().toLowerCase();
  return `slug:${contact.tenantCode.trim().toLowerCase()}|${slug}`;
}

function isExpired(storedAt: number, nowMs: number): boolean {
  return nowMs - storedAt > PENDING_SP_CONTACTS_TTL_MS;
}

function readRaw(): StoredPendingSpContact[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PENDING_SP_CONTACTS_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is StoredPendingSpContact => {
      if (item === null || typeof item !== 'object') {
        return false;
      }
      const row = item as StoredPendingSpContact;
      return (
        typeof row.tenantCode === 'string' &&
        row.tenantCode.length > 0 &&
        typeof row.seenAt === 'string' &&
        typeof row.storedAt === 'number'
      );
    });
  } catch {
    return [];
  }
}

function writeRaw(items: StoredPendingSpContact[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PENDING_SP_CONTACTS_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota / private mode */
  }
}

function pruneAndDedupe(
  items: StoredPendingSpContact[],
  nowMs: number,
): StoredPendingSpContact[] {
  const fresh = items.filter((item) => !isExpired(item.storedAt, nowMs));
  const byKey = new Map<string, StoredPendingSpContact>();
  for (const item of fresh) {
    byKey.set(dedupeKey(item), item);
  }
  const deduped = Array.from(byKey.values()).sort((a, b) => b.storedAt - a.storedAt);
  return deduped.slice(0, PENDING_SP_CONTACTS_MAX);
}

/** Read pending contacts (TTL-pruned, deduped, capped). */
export function readPendingSpContacts(nowMs: number = Date.now()): PendingSpContactRef[] {
  const existing = readRaw();
  const pruned = pruneAndDedupe(existing, nowMs);
  if (pruned.length !== existing.length) {
    writeRaw(pruned);
  }
  return pruned.map(({ tenantCode, salesPointId, salesPointSlug, seenAt }) => ({
    tenantCode,
    seenAt,
    ...(typeof salesPointId === 'number' ? { salesPointId } : {}),
    ...(typeof salesPointSlug === 'string' && salesPointSlug.length > 0
      ? { salesPointSlug }
      : {}),
  }));
}

/** Stage a contact while browsing without access token. */
export function stagePendingSpContact(input: {
  tenantCode: string;
  salesPointId?: number;
  salesPointSlug?: string;
  seenAt?: string;
  nowMs?: number;
}): void {
  const nowMs = input.nowMs ?? Date.now();
  const seenAt = input.seenAt ?? new Date(nowMs).toISOString();
  const next: StoredPendingSpContact = {
    tenantCode: input.tenantCode,
    seenAt,
    storedAt: nowMs,
    ...(typeof input.salesPointId === 'number' && input.salesPointId > 0
      ? { salesPointId: input.salesPointId }
      : {}),
    ...(typeof input.salesPointSlug === 'string' && input.salesPointSlug.trim().length > 0
      ? { salesPointSlug: input.salesPointSlug.trim() }
      : {}),
  };
  if (
    next.salesPointId === undefined &&
    (next.salesPointSlug === undefined || next.salesPointSlug.length === 0)
  ) {
    return;
  }
  const merged = pruneAndDedupe([next, ...readRaw()], nowMs);
  writeRaw(merged);
}

/** Replace storage after claim — keep only contacts not in activated refs. */
export function removeActivatedPendingSpContacts(
  activated: PendingSpContactRef[],
  nowMs: number = Date.now(),
): void {
  if (activated.length === 0) {
    writeRaw(pruneAndDedupe(readRaw(), nowMs));
    return;
  }
  const activatedKeys = new Set(activated.map(dedupeKey));
  const remaining = pruneAndDedupe(readRaw(), nowMs).filter(
    (item) => !activatedKeys.has(dedupeKey(item)),
  );
  writeRaw(remaining);
}

export function clearPendingSpContacts(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_SP_CONTACTS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
